import { ActionIcon, Stack, Text, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Role, TruckSimple, useDeleteTruckModelMutation, useGetTruckModelsQuery } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_trucks/caminhoes/modelos/")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Modelos de Caminhões" }] }),
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const { data = [], isFetching, refetch } = useGetTruckModelsQuery();
  const [deleteTruckModel, { isLoading: isDeleting }] = useDeleteTruckModelMutation();

  const columns: GridColDef<TruckSimple>[] = [
    { field: "id", headerName: "ID", width: 90, align: "center", headerAlign: "center" },
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "actions",
      headerName: "Ações",
      width: 150,
      type: "actions",
      renderCell: ({ row }) => {
        const { id, name } = row;

        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Modelo">
              <ActionIcon variant="light" color="yellow" onClick={() => handleEdit({ id, model: name })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Modelo">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete({ id })}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  const handleEdit = (body: { id: number; model: string }) => {
    MODAL_HANDLER.createEdit.truck.model(body);
  };

  const handleDelete = (body: { id: number }) => {
    modals.openConfirmModal({
      title: "Excluir Modelo",
      children: (
        <Stack className="text-center">
          <Title order={4}>Tem certeza que deseja excluir este modelo?</Title>
          <Text c="dimmed">Esta ação não pode ser desfeita.</Text>
        </Stack>
      ),
      onConfirm: async () => await deleteTruckModel(body),
      cancelProps: { disabled: isDeleting },
      confirmProps: { color: "red", loading: isDeleting },
      groupProps: { className: cn("grid! grid-cols-1 gap-[inherit] md:grid-cols-2") },
      labels: { confirm: "Deletar", cancel: "Cancelar" }
    });
  };

  return (
    <Stack>
      <div className="grid grid-cols-[1fr_auto] gap-[inherit]">
        <DashboardComponents.Title>Modelos de Caminhões</DashboardComponents.Title>

        <div className="flex items-center justify-center gap-2">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Modelos" }} onClick={refetch} loading={isFetching} />

          <Tooltip label="Cadastrar novo Modelo de Caminhão">
            <ActionIcon variant="filled" size="xl" onClick={() => MODAL_HANDLER.createEdit.truck.model()}>
              <PlusIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />
    </Stack>
  );
}
